import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { SelfProfile, UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { TextField } from '../../ui/TextField';
import { OffButton } from '../../ui/OffButton';
import { isStill, reduced } from '../../ui/motion';
import { StudioCreateButton } from '../studio/StudioFormParts';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { StudioHeaderContent } from '../studio/StudioHeaderContent';
import { StudioIcon } from '../studio/StudioIcon';
import type { StudioApp } from '../studio/studio-api';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { record } from '../studio/studio-content';
import { JSON_CAST, castFromInputs, castMissing, castToInputs, castWords, firstEmpty, fitCast, type Cast, type Performer } from './reels-cast';
import { photoNeedText, reelsMissing, reelsSupported } from './reels-form-model';
import {
  aspectWord,
  mmss,
  multiPrice,
  objectKind,
  objectNeeds,
  objectWords,
  plural,
  reelsPolicy,
  screenRoles,
  type ReelsMode,
  type ReelsPolicy,
  type ReelsTemplate,
} from './reels-policy';
import { ReelsCast } from './ReelsCast';
import { ReelsCatalog } from './ReelsCatalog';
import { ReelsHero, type HeroFrame } from './ReelsHero';
import { ReelsOrderResult } from './ReelsOrderResult';
import { ReelsResultImport } from './ReelsResultImport';
import { useReelsPerformers } from './use-reels-performers';
import { keyInfo, patchInputs, readKey, readList, readText } from './form-keys';
import { ReelsObjectsJson, type JsonObjectNeed } from './ReelsObjectsJson';
import '../studio/native-template.css';
import './reels-native.css';

interface Props {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}
interface People {
  performers: Performer[];
  self?: SelfProfile | null;
  loaded: boolean;
  error: boolean;
  retry(): void;
}
const noPeople: People = { performers: [], loaded: true, error: false, retry: () => {} };

/** «Рилс с вами» и «Вы в кино» (APP-SPEC-5 §8): каталог, экран шаблона, набор, пакет, сцена и хвост заказа.
    Каждый контрол стоит, только когда опубликованная форма даёт его поле; заказ уходит общим durable-хуком. */
export function ReelsOrderForm({ app, avatar, initialInputs, headerActive = true }: Props) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const castPublished = !!state.form?.fields.some(field => /^cast_/.test(field.key) && field.type === 'cast') || !!keyInfo(state.form, JSON_CAST);
  return castPublished ? (
    <WithPerformers avatar={avatar}>
      {people => <ReelsScreen state={state} app={app} avatar={avatar} people={people} initialInputs={initialInputs} headerActive={headerActive} />}
    </WithPerformers>
  ) : (
    <ReelsScreen state={state} app={app} avatar={avatar} people={noPeople} initialInputs={initialInputs} headerActive={headerActive} />
  );
}
function WithPerformers({ avatar, children }: { avatar: UserAvatar | null; children(people: People): ReactNode }) {
  const people = useReelsPerformers(avatar, true);
  return <>{children(people)}</>;
}

function templatesOf(policy: ReelsPolicy, mode: ReelsMode, ids: string[], id: string): ReelsTemplate[] {
  if (mode === 'multi') return ids.flatMap(value => policy.templates.filter(t => t.id === value));
  if (mode === 'one') {
    const chosen = policy.templates.find(t => t.id === id);
    return chosen ? [chosen] : policy.template ? [policy.template] : [];
  }
  if (mode === 'app') return policy.template ? [policy.template] : [];
  return [];
}

function ReelsScreen({
  state,
  app,
  avatar,
  people,
  initialInputs,
  headerActive,
}: {
  state: CircleFormState;
  app: StudioApp;
  avatar: UserAvatar | null;
  people: People;
  initialInputs?: Record<string, unknown>;
  headerActive: boolean;
}) {
  const { form, inputs, locked, orderId } = state;
  const supported = !!form && reelsSupported(form);
  const policy = useMemo(() => (form ? reelsPolicy(form, app) : null), [form, app]);
  const modeField = keyInfo(form, 'mode');
  const idField = keyInfo(form, 'template_id');
  const idsField = keyInfo(form, 'template_ids');
  const chosenMode = readText(modeField, inputs);
  const chosenId = readText(idField, inputs);
  const chosenIds = readList(idsField, inputs);
  const offers = (value: ReelsMode) => (modeField && (!modeField.values.length || modeField.values.includes(value)) ? value : null);
  const hasCatalog = !!policy?.templates.length && !!(idField || idsField || modeField?.values.includes('pack'));
  const known = (value: string): value is ReelsMode =>
    !!modeField && (modeField.values.length ? modeField.values.includes(value) : ['one', 'multi', 'pack', 'app'].includes(value));
  // Режим: выбранный во входах → сцена открытого шаблона (`app_cast`, reelsPolicy) → `default_value` ключа `mode`
  // (кроме «один шаблон» без шаблонов в политике: тогда выбирать нечего) → по опубликованным ключам.
  const fallback: ReelsMode = idsField && chosenIds.length > 1 ? 'multi' : idField || idsField ? 'one' : 'app';
  const preset = modeField?.defaultValue ?? '';
  const mode: ReelsMode = known(chosenMode)
    ? chosenMode
    : (policy?.mode ?? (known(preset) && (preset !== 'one' || !!policy?.templates.length || !!idField) ? preset : fallback));
  const templates = policy ? templatesOf(policy, mode, chosenIds, chosenId || (mode === 'one' && chosenIds.length === 1 ? chosenIds[0] : '')) : [];
  const { roles, orphan } = form && policy ? screenRoles(form, policy, mode, templates) : { roles: [], orphan: null };
  const cast = castFromInputs(roles, inputs);
  const roleKey = roles.map(r => r.id).join();
  const [view, setView] = useState<'cat' | 'pre' | null>(null);
  const [category, setCategory] = useState<string>();
  const [focus, setFocus] = useState('');
  const shownView = view ?? (hasCatalog && mode !== 'app' && !chosenId && !chosenIds.length && chosenMode !== 'pack' && !orderId ? 'cat' : 'pre');
  const openRole = typeof initialInputs?.open_role === 'string' ? initialInputs.open_role : '';
  const seeded = useRef('');
  const [reveal, setReveal] = useState(0);
  // Состав после загрузки людей и при смене шаблона: роли с тем же id остаются за теми же людьми,
  // «Вы» встаёт в первую роль self_default; артефакт префилла (character:<id варианта> во всех ролях) снимается.
  useEffect(() => {
    if (!form || !people.loaded || !roles.length || state.locked) return;
    const signature = `${form.app_id}:${roleKey}`;
    const first = seeded.current !== signature;
    seeded.current = signature;
    const cleaned: Cast = Object.fromEntries(
      Object.entries(cast).map(([id, refs]) => [
        id,
        refs.filter(ref => !(avatar && ref === `character:${avatar.id}` && initialInputs?.[`cast_${id}`] !== ref)),
      ]),
    );
    const next = fitCast(roles, cleaned, people.performers, first);
    const before = JSON.stringify(castToInputs(roles, cast, {}));
    const after = JSON.stringify(castToInputs(roles, next, {}));
    if (before !== after) state.setInputs(current => castToInputs(roles, next, current));
    if (first) setFocus(roles.some(r => r.id === openRole) ? openRole : (firstEmpty(roles, next) ?? roles[0].id));
    // Сцена шаблона и вход «Выбрать, кто играет» (openRole эталона): блок «Кто в кадре» — в видимую часть колонки.
    if (first && (mode === 'app' || openRole)) setReveal(value => value + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, people.loaded, people.performers, roleKey]);
  // Обязательный ключ `mode` контракта (docs/59 §4): режим сцены, выведенный из ролей шаблона, уходит в заказ.
  const sceneMode = !!form && policy?.mode === 'app' && mode === 'app' && !chosenMode && offers('app') === 'app';
  useEffect(() => {
    if (sceneMode && !state.locked && !orderId) state.setInputs(current => patchInputs(form, current, { mode: 'app' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneMode, orderId]);
  const photoSlots = supported ? form.fields.filter(item => item.upload?.kind === 'photo') : [];
  const previews = useStudioPhotoPreviews(state, photoSlots);
  // Лоток вида стоит, только если он нужен выбранным шаблонам (у шаблона «с вами» лотка нет); без сведений о шаблоне — все лотки формы.
  const needs = (form && policy && supported ? objectNeeds(form, policy, templates) : []).filter(
    need =>
      mode !== 'pack' &&
      (!templates.length || templates.every(t => !t.need) || templates.some(t => t.need === need.kind || objectKind(t.need) === need.kind)),
  );
  // JSON-ключ `objects` контракта экрана: лоток вида, который нужен шаблону и не покрыт полями шаблона.
  const objectsInfo = keyInfo(form, 'objects');
  const objectsValue = record(readKey(objectsInfo, inputs));
  const jsonNeeds: JsonObjectNeed[] =
    objectsInfo && policy && mode !== 'pack'
      ? [...new Set(templates.map(t => objectKind(t.need)).filter(kind => ['goods', 'food', 'place', 'pics'].includes(kind)))]
          .filter(kind => !needs.some(need => need.kind === kind))
          .map(kind => {
            const limits = templates.filter(t => objectKind(t.need) === kind && t.photos).map(t => t.photos!);
            const words = { ...objectWords.pics, ...objectWords[kind] };
            const max = Math.min(policy.maxObjectPhotos, limits.length ? Math.max(...limits.map(l => l.max)) : policy.maxObjectPhotos);
            return { kind, label: policy.needs[kind]?.label || words.label, tip: policy.needs[kind]?.tip || words.tip, of: words.of, min: limits.length ? Math.min(max, Math.max(...limits.map(l => l.min))) : 1, max };
          })
      : [];
  const objectIds = (kind: string) => (Array.isArray(objectsValue[kind]) ? (objectsValue[kind] as unknown[]).filter((x): x is string => typeof x === 'string') : []);
  const scenario = supported ? form.fields.find(item => item.key === 'text') : undefined;
  const legacy = !!form && supported && !roles.length && !needs.some(n => n.kind !== 'pics') && !hasCatalog && !Object.keys(record(record(form.constraints).reels)).length;
  const variants = policy?.variants ?? null;
  const pack = policy?.pack ?? null;
  const multiFrom = policy?.multi?.discountFrom ?? Infinity;
  const pct = policy?.multi?.discountPct ?? 0;
  const lead = templates[0];
  // Цена выбора из каталога; без неё — сумма, которую use-studio-form шлёт в заказ (state.price).
  const price =
    !form || !policy
      ? 0
      : mode === 'multi'
        ? (multiPrice(policy, templates) ?? state.price)
        : mode === 'pack'
          ? (pack?.price ?? state.price)
          : mode === 'one' && lead
            ? (lead.price ?? policy.types[lead.type]?.price ?? state.price)
            : state.price;
  // На кнопке — сумма заказа: без списания цена каталога её не меняет (как orderPrice в shoot/kit/form-model).
  const shownPrice = form?.chargeable === false ? state.price : price;
  const name =
    mode === 'multi'
      ? uiFormat('{0} {1}', templates.length, plural(templates.length, 'шаблон', 'шаблона', 'шаблонов'))
      : mode === 'pack'
        ? (pack?.title ?? app.name)
        : (lead?.title ?? app.name);
  const desc =
    mode === 'multi'
      ? Number.isFinite(multiFrom)
        ? templates.length >= multiFrom
          ? uiFormat('Скидка {0}% за набор', pct)
          : uiFormat('От {0} шаблонов скидка {1}%', multiFrom, pct)
        : ''
      : mode === 'pack'
        ? uiFormat('{0} разных сюжетов с вами за один заказ', pack?.count ?? 10)
        : mode === 'app'
          ? lead?.plot || uiFormat('Вы в истории шаблона «{0}»', name)
          : (lead?.plot ?? '');
  const duration = mode === 'multi' ? Math.max(0, ...templates.map(t => t.durationS ?? 0)) || null : mode === 'pack' ? (pack?.durationS ?? null) : (lead?.durationS ?? null);
  const many = mode === 'multi' || mode === 'pack';
  // Строка сведений и подзаголовок шапки — как у эталона (infoHTML, unitHTML): числа моноширинным.
  const mono = (value: ReactNode) => <span className="mono">{value}</span>;
  const infoLead: ReactNode =
    mode === 'pack' ? (
      <>
        {mono(pack?.count ?? 10)} {uiText('роликов')}
      </>
    ) : mode === 'multi' ? (
      variants ? (
        <>
          {uiText('По')} {mono(variants)} {uiText('варианта')}
        </>
      ) : null
    ) : duration !== null ? (
      mono(mmss(duration))
    ) : null;
  const infoRest = [aspectWord(lead?.aspect ?? '9:16'), uiText(many ? 'музыка и монтаж уже в шаблонах' : 'музыка и монтаж уже в шаблоне')].join(' · ');
  const unit: ReactNode =
    mode === 'pack' ? (
      <>
        {mono(pack?.count ?? 10)} {uiText('разных рилсов')}
      </>
    ) : !variants ? null : mode === 'multi' ? (
      <>
        {uiText('по')} {mono(variants)} {uiText('варианта каждого шаблона')}
        {templates.length >= multiFrom && (
          <>
            {' · '}
            {uiText('скидка')} {mono(`${pct}%`)}
          </>
        )}
      </>
    ) : (
      <>
        {mono(variants)} {uiText(`${plural(variants, 'вариант', 'варианта', 'вариантов')} в заказе`)}
      </>
    );
  // Кадры примера с ключами эталона (pvList): шаблон — d<id><N>; сцена — обложка, которую рамка проходит слева
  // направо (dapp<стиль>_<N>, три положения), набор и пакет — по кадру на шаблон (m<id>).
  const frames: HeroFrame[] =
    mode === 'multi'
      ? templates.flatMap(t => (t.frames[0] ? [{ url: t.frames[0], label: t.title, key: `m${t.id}` }] : []))
      : mode === 'pack'
        ? (policy?.templates ?? []).slice(0, 10).flatMap(t => (t.frames[0] ? [{ url: t.frames[0], label: t.title, key: `m${t.id}` }] : []))
        : mode === 'app' && lead?.frames.length === 1
          ? [42, 50, 58].map((x, i) => ({ url: lead.frames[0], key: `dapp0_${i}`, pos: `${x}% 20%` }))
          : (lead?.frames ?? []).map((url, i) => ({ url, key: mode === 'app' ? `dapp0_${i}` : `d${lead!.id}${i}` }));
  const setInputs = (patch: Record<string, string | string[] | Record<string, unknown> | null>) => state.setInputs(current => patchInputs(form, current, patch));
  const openOne = (id: string) => {
    if (!idField && !idsField) return;
    setInputs(idField ? { template_id: id, template_ids: null, mode: offers('one') } : { template_ids: [id], mode: offers('one') });
    setView('pre');
  };
  const missing = (() => {
    if (!form) return 'Проверяем доступность';
    if (!supported) return 'Этот вариант рилса пока недоступен';
    if (orphan) return uiFormat('Роль «{0}» пока недоступна', orphan);
    if (mode === 'multi' && templates.length < (policy?.multi?.min ?? 2)) return 'Отметьте ещё один шаблон';
    if (mode === 'one' && idField && !lead) return 'Выберите шаблон';
    if (roles.length) {
      if (people.error) return 'Не удалось загрузить людей';
      if (!people.loaded) return 'Загружаем людей…';
      const gap = castMissing(roles, cast, people.performers);
      if (gap) return gap;
    }
    // Права и загрузки — только у видимых лотков; сценарий и проверка id загрузки — общими правилами формы.
    const formGap = reelsMissing(form, inputs, true);
    if (needs.some(need => need.slots.some(slot => slot.required || inputs[slot.key])) && !state.rights) return 'Подтвердите права на фото';
    if (formGap === 'Проверьте загрузку фото') return formGap;
    for (const need of needs) {
      const have = need.slots.filter(slot => inputs[slot.key]).length;
      if (have < need.min) return photoNeedText(have, need.min, need.of);
    }
    if (jsonNeeds.length && !state.rights) return 'Подтвердите права на фото';
    for (const need of jsonNeeds) {
      const have = objectIds(need.kind).length;
      if (have < need.min) return photoNeedText(have, need.min, need.of);
    }
    if (formGap && !formGap.startsWith('Загрузите')) return formGap;
    // Сверка с суммой заказа; без списания сверять нечего — сервер всё равно проверит expected_price.
    if (form.chargeable !== false && price !== state.price) return 'Цена этого выбора пока не подтверждена';
    return null;
  })();
  const submit = () => {
    if (!state.busy && !state.submissionBlocked && (state.intent || (supported && form?.available && !missing))) void state.submit();
  };
  const ctaLabel =
    legacy || !supported
    ? undefined
    : mode === 'pack'
      ? uiFormat('Создать {0} рилсов', pack?.count ?? 10)
      : mode === 'multi'
        ? uiFormat('Создать {0} {1}', templates.length, plural(templates.length, 'рилс', 'рилса', 'рилсов'))
        : 'Создать';
  const header = legacy ? app.name : policy?.scene ? 'Вы в кино' : 'Рилс с вами';
  const performers = people.performers;
  const groups = mode === 'multi' ? Object.fromEntries(templates.map(t => [t.id, t.title])) : undefined;
  // Экран шаблона в разметке эталона (bot-reels.js pageHTML): тело `.pf-body.rl-body` в колонке `.st-in`, подвал,
  // затемнение и панель листа — на уровне `#studio`. Прежний воркфлоу (`text`/`photo`), неподдержанная форма,
  // каталог и хвост заказа остаются в своей обёртке.
  const scene = !!form && supported && !legacy && !orderId && shownView === 'pre';
  const bodyRef = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!scene || !headerActive || !body) return setHost(null);
    // Колонка экрана — та же `.st-in` студии: классы формы эталона (`pf-in rl-in`) держатся, пока экран шаблона открыт.
    const column = body.closest<HTMLElement>('.st-in');
    const added = ['pf-in', 'rl-in'].filter(name => !column?.classList.contains(name));
    column?.classList.add(...added);
    setHost(body.closest<HTMLElement>('#studio'));
    return () => column?.classList.remove(...added);
  }, [scene, headerActive]);
  // Нижний отступ колонки под подвал (BotCore.foot pad): короткая форма — без отступа, иначе низ тела встаёт
  // на 16 px выше кнопки (над меню на телефоне). Тело наблюдается: люди и лотки догружаются — отступ пересчитывается.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const column = body?.closest<HTMLElement>('.st-in');
    if (!scene || !headerActive || !body || !column) return;
    const pad = () => {
      const mob = !!column.closest('.bot.mob');
      const need = (mob ? 84 : 16) + 58 + 16;
      const pb = parseFloat(getComputedStyle(body).paddingBottom) || 0;
      const bottom = body.getBoundingClientRect().bottom - pb - column.getBoundingClientRect().top + column.scrollTop;
      const px = Math.max(0, need - pb);
      column.style.setProperty('--ap-pad', bottom > column.clientHeight - need ? (mob ? `calc(${px}px + var(--sab,0px))` : `${px}px`) : '0px');
    };
    pad();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pad);
    observer?.observe(body);
    window.addEventListener('resize', pad);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', pad);
      column.style.removeProperty('--ap-pad');
    };
  }, [scene, headerActive]);
  // Блок «Кто в кадре» в видимую часть колонки (blockIn bot-people.js): низ — над кнопкой и меню, верх — не под шапкой.
  useEffect(() => {
    if (!reveal) return;
    const timer = window.setTimeout(() => {
      const block = bodyRef.current?.querySelector<HTMLElement>('.pp-castc');
      if (!block) return;
      let scroller: HTMLElement | null = block.parentElement;
      while (scroller && scroller !== document.body) {
        if (/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight + 2) break;
        scroller = scroller.parentElement;
      }
      if (!scroller || scroller === document.body) return;
      const box = scroller.getBoundingClientRect();
      const y = (el: Element) => el.getBoundingClientRect().top - box.top + scroller.scrollTop;
      const label = block.previousElementSibling?.classList.contains('glbl') ? block.previousElementSibling : block;
      const top = Math.max(0, Math.min(y(label) - 84, y(block) + block.offsetHeight - scroller.clientHeight + 170));
      if (top > scroller.scrollTop + 2) scroller.scrollTo({ top, behavior: reduced() || isStill() ? 'auto' : 'smooth' });
    }, 380);
    return () => clearTimeout(timer);
  }, [reveal]);
  const castBlock = (
    <>
      {roles.length > 0 && people.error && (
        <p role="alert">
          {uiText('Не удалось загрузить людей и персонажей.')}{' '}
          <button type="button" onClick={people.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {roles.length > 0 && !people.loaded && <p role="status">{uiText('Загружаем людей…')}</p>}
      {roles.length > 0 && people.loaded && !people.error && (
        <ReelsCast
          roles={roles}
          cast={cast}
          performers={performers}
          focus={roles.some(r => r.id === focus) ? focus : roles[0].id}
          locked={locked}
          groups={groups}
          self={people.self}
          onReload={people.retry}
          onFocus={setFocus}
          onChange={(next, nextFocus) => {
            state.setInputs(current => castToInputs(roles, next, current));
            setFocus(nextFocus);
          }}
        />
      )}
    </>
  );
  const inputsBlock = (
    <>
      {needs.map((need, index) => {
        const have = need.slots.filter(slot => inputs[slot.key]).length;
        return (
          <div key={need.kind} className="rl-w">
            <StudioPhotoTray
              state={state}
              slots={need.slots}
              urls={previews.urls}
              minimum={need.min}
              title={need.label}
              description={need.tip}
              consent={
                index === 0 && (
                  <label className="photo-consent">
                    <input type="checkbox" checked={state.rights} disabled={locked} onChange={event => state.setRights(event.target.checked)} />
                    {uiText('Подтверждаю права на фото и согласие людей на нём')}
                  </label>
                )
              }
            />
            <p className="rl-tip">{uiText(have > need.max ? uiFormat('В рилс войдут первые {0} фото', need.max) : need.tip)}</p>
          </div>
        );
      })}
      {previews.error && (
        <p role="alert">
          {uiText('Не удалось загрузить превью.')} <button type="button" onClick={previews.retry}>{uiText('Повторить')}</button>
        </p>
      )}
      {jsonNeeds.map((need, index) => (
        <ReelsObjectsJson
          key={need.kind}
          need={need}
          ids={objectIds(need.kind)}
          locked={locked}
          rights={state.rights}
          consent={
            index === 0 && !needs.length ? (
              <label className="photo-consent">
                <input type="checkbox" checked={state.rights} disabled={locked} onChange={event => state.setRights(event.target.checked)} />
                {uiText('Подтверждаю права на фото и согласие людей на нём')}
              </label>
            ) : undefined
          }
          onChange={ids => setInputs({ objects: { ...objectsValue, [need.kind]: ids } })}
        />
      ))}
      <ReelsResultImport state={state} />
      {scenario && (
        <TextField
          label={uiText('Сценарий')}
          value={inputs.text ?? ''}
          limit={scenario.max_length ?? 1000}
          disabled={locked}
          onChange={value => state.setInputs(current => ({ ...current, text: value }))}
        />
      )}
      {supported && legacy && <p className="hint">{uiText('Монтаж и музыка заданы шаблоном.')}</p>}
      {form?.chargeable === false && <p>{uiText('Без списания')}</p>}
    </>
  );
  const sceneFoot = form && (
    <ReelsFoot
      label={ctaLabel ?? 'Создать'}
      price={shownPrice}
      busy={state.busy}
      blocked={state.submissionBlocked}
      pending={!!state.intent}
      missing={missing}
      available={form.available && supported}
      onCreate={submit}
    />
  );
  return (
    <>
      <StudioHeaderContent enabled={headerActive}>
        {legacy ? (
          <div className="bc-tt">
            <h2>{uiText(header)}</h2>
          </div>
        ) : (
          <>
            <h2 className="pf-title">{uiText(header)}</h2>
            {unit && shownView === 'pre' && <small className="bc-unit">{unit}</small>}
          </>
        )}
      </StudioHeaderContent>
      {scene ? (
        <>
          <div ref={bodyRef} className="pf-body rl-body">
            {hasCatalog && mode !== 'app' && (
              <button type="button" className="lnk rl-back" disabled={locked} onClick={() => setView('cat')}>
                <StudioIcon kind="back" />
                {uiText('Все шаблоны')}
              </button>
            )}
            <ReelsHero
              frames={frames}
              type={mode === 'multi' || mode === 'pack' ? '' : (lead?.type ?? '')}
              durationS={duration}
              badge={mode === 'pack' ? (pack?.badge ?? null) : mode === 'multi' ? null : (lead?.badge ?? null)}
              deck={many}
              ghosts={frames.slice(1, 3).map(frame => frame.url)}
            />
            <div className="pf-who rl-who">
              <b>{name}</b>
              {desc && <span>{desc}</span>}
            </div>
            <p className="rl-info">
              {infoLead}
              {infoLead && ' · '}
              {infoRest}
            </p>
            {mode === 'multi' && templates.length > 0 && (
              <div className="rl-pre nosb" aria-label={uiText('Шаблоны в заказе')}>
                {templates.map(t => (
                  <span key={t.id} className="rl-pc sty sel">
                    <span className="th">{t.frames[0] && <img src={t.frames[0]} alt="" draggable={false} />}</span>
                    {t.title}
                    <button
                      className="rx"
                      type="button"
                      disabled={locked}
                      aria-label={uiFormat('Убрать «{0}»', t.title)}
                      onClick={() => {
                        const rest = templates.filter(x => x.id !== t.id).map(x => x.id);
                        // Останется один — его экран (если форма публикует одиночный шаблон).
                        if (rest.length === 1) openOne(rest[0]);
                        else if (!rest.length) setView('cat');
                        else setInputs({ template_ids: rest });
                      }}
                    >
                      <StudioIcon kind="close" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {castBlock}
            {inputsBlock}
          </div>
          {host ? (
            createPortal(
              <>
                {sceneFoot}
                <div className="bscrim" aria-hidden="true" />
                <aside className="side bsheet pf-sheet rl-sheet" role="dialog" aria-label={uiText('Рилс')} aria-hidden="true" />
              </>,
              host,
            )
          ) : (
            sceneFoot
          )}
        </>
      ) : (
        <section className="native-studio-form server-reels rl-in">
          {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
          {state.error && (
            <p role="alert">
              {uiText(state.error)}{' '}
              <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
                {uiText('Повторить')}
              </button>
            </p>
          )}
          {form && !orderId && shownView === 'cat' && policy && (
            <ReelsCatalog
              policy={policy}
              allowed={idField?.values ?? idsField?.values ?? []}
              canMulti={!!idsField}
              canPack={offers('pack') === 'pack' && !!pack}
              initialCategory={category}
              onOpen={(id, cat) => {
                setCategory(cat);
                openOne(id);
              }}
              onOpenMulti={(ids, cat) => {
                setCategory(cat);
                setInputs({ template_ids: ids, template_id: null, mode: offers('multi') });
                setView('pre');
              }}
              onOpenPack={cat => {
                setCategory(cat);
                setInputs({ mode: 'pack', template_id: null, template_ids: null });
                setView('pre');
              }}
            />
          )}
          {form && !orderId && shownView === 'pre' && (
            <form
              onSubmit={event => {
                event.preventDefault();
                submit();
              }}
            >
              {!supported && <p role="status">{uiText('Этот вариант рилса пока недоступен')}</p>}
              {hasCatalog && (
                <button type="button" className="lnk rl-back" disabled={locked} onClick={() => setView('cat')}>
                  <StudioIcon kind="back" />
                  {uiText('Все шаблоны')}
                </button>
              )}
              {castBlock}
              {inputsBlock}
              <StudioCreateButton
                kind="video"
                label={ctaLabel}
                price={shownPrice}
                busy={state.busy}
                blocked={state.submissionBlocked}
                pending={!!state.intent}
                missing={missing}
                available={form.available && supported}
                cheap={null}
                onCreate={submit}
              />
            </form>
          )}
          {orderId && (
            <ReelsOrderResult
              state={state}
              mode={legacy ? 'legacy' : mode}
              title={name}
              card={[name, castWords(roles, cast, performers), duration !== null ? mmss(duration) : '', aspectWord(lead?.aspect ?? '9:16')].filter(Boolean).join(' · ')}
              morePrice={group => {
                if (!policy?.moreAvailable || mode === 'pack') return null;
                const t = templates.find(x => x.id === group) ?? (templates.length === 1 ? templates[0] : undefined);
                const own = t ? (t.morePrice ?? policy.types[t.type]?.morePrice ?? null) : null;
                return own ?? policy.morePrice;
              }}
              onOtherTemplate={hasCatalog ? () => setView('cat') : undefined}
            />
          )}
        </section>
      )}
    </>
  );
}

/** Подвал экрана шаблона (BotCore.foot эталона): «Создать» стоит всегда; пока чего-то не хватает — неактивна,
    нажатие — дрожь и подсказка на месте подписи. Та же логика, что у общей StudioCreateButton, без её обёртки. */
function ReelsFoot({
  label,
  price,
  busy,
  blocked,
  pending,
  missing,
  available,
  onCreate,
}: {
  label: string;
  price: number;
  busy: boolean;
  blocked: boolean;
  pending: boolean;
  missing: string | null;
  available: boolean;
  onCreate(): void;
}) {
  const miss = blocked ? 'Проверяем доступность' : pending ? undefined : missing || (!available ? 'Создание сейчас недоступно' : undefined);
  return (
    <div className="ap-foot">
      <OffButton
        className="fin"
        disabled={busy}
        label={busy ? uiText('Отправляем…') : pending ? uiText('Продолжить проверку') : uiFormat('{0} · {1} C', uiText(label), price)}
        miss={miss && uiText(miss)}
        onClick={() => {
          if (busy || blocked) return;
          if (pending) return onCreate();
          if (missing || !available) return;
          onCreate();
        }}
      />
    </div>
  );
}
